export type TooltipSide = "top" | "right" | "bottom" | "left";

interface TooltipSize {
  height: number;
  width: number;
}

interface ViewportSize {
  height: number;
  width: number;
}

export interface TooltipPosition {
  left: number;
  side: TooltipSide;
  top: number;
}

const SIDES: Record<TooltipSide, TooltipSide[]> = {
  top: ["top", "bottom", "right", "left"],
  right: ["right", "left", "top", "bottom"],
  bottom: ["bottom", "top", "right", "left"],
  left: ["left", "right", "top", "bottom"],
};

export function resolveTooltipPosition(
  anchor: DOMRect,
  size: TooltipSize,
  preferredSide: TooltipSide,
  viewport: ViewportSize,
  gap = 8,
  gutter = 12,
): TooltipPosition {
  const candidates = SIDES[preferredSide].map((side) => ({
    side,
    ...positionForSide(anchor, size, side, gap),
  }));
  const candidate =
    candidates.find((item) => fitsViewport(item, size, viewport, gutter)) ??
    candidates[0];

  return {
    left: clamp(candidate.left, gutter, viewport.width - size.width - gutter),
    side: candidate.side,
    top: clamp(candidate.top, gutter, viewport.height - size.height - gutter),
  };
}

function positionForSide(
  anchor: DOMRect,
  size: TooltipSize,
  side: TooltipSide,
  gap: number,
) {
  const centerX = anchor.left + anchor.width / 2;
  const centerY = anchor.top + anchor.height / 2;

  if (side === "bottom") {
    return { left: centerX - size.width / 2, top: anchor.bottom + gap };
  }
  if (side === "left") {
    return {
      left: anchor.left - size.width - gap,
      top: centerY - size.height / 2,
    };
  }
  if (side === "right") {
    return { left: anchor.right + gap, top: centerY - size.height / 2 };
  }
  return {
    left: centerX - size.width / 2,
    top: anchor.top - size.height - gap,
  };
}

function fitsViewport(
  position: TooltipPosition,
  size: TooltipSize,
  viewport: ViewportSize,
  gutter: number,
) {
  return (
    position.left >= gutter &&
    position.top >= gutter &&
    position.left + size.width <= viewport.width - gutter &&
    position.top + size.height <= viewport.height - gutter
  );
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(value, Math.max(minimum, maximum)));
}
